import React, { useState, useEffect, useContext } from 'react';
import { Button, Table, Modal, Spinner, Alert, Form } from 'react-bootstrap';
import { AdminClient } from "../web-api-client.ts";
import useToast from '../hooks/UseToast.js';
import BackButton from './BackButton.js';
import Loader from './Loader.js';
import UserContext from '../providers/UserContext.js';

const UsersPage = () => {
  const currentUser = useContext(UserContext);
  // Defensive guard — the nav link is already hidden for non-admins, but
  // technicians could still hit this URL directly. Server-side hardening
  // of /api/Admin/* for non-admins is recommended next.
  const isDealerAdmin = !!(currentUser?.roles && currentUser.roles.includes('Dealer Admin'));
  const { showToast } = useToast();
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);  // Holds the available roles from API
  const [showModal, setShowModal] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);  // Flag for new user vs editing existing
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [userName, setUserName] = useState('');
  const [email, setEmail] = useState('');
  const [initialPassword, setInitialPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPasswordField, setShowPasswordField] = useState(false); // To toggle password field visibility
  const [passwordError, setPasswordError] = useState(false);
  const [initialPasswordError, setInitialPasswordError] = useState(false);
  const [error, setError] = useState(null);




  // Fetching users and available roles when component mounts
  useEffect(() => {
    const fetchUsersAndRoles = async () => {
      setLoading(true);
      try {
        const usersData = await getUsers();
        setUsers(usersData);

        const rolesData = await getRoles();  // Fetch roles from the API
        setAvailableRoles(rolesData);
      } catch (err) {
        showToast('Failed to load users or roles.', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchUsersAndRoles();
  }, []);

  // Fetch user details and roles when Edit button is clicked
  const handleEditUser = async (userId) => {
    setLoading(true);
    setIsNewUser(false);  // We're editing an existing user
    try {
      const userData = await getUserById(userId);
      const userRoles = await getUserRolesByUserId(userId);
      setSelectedUser(userData);
      setSelectedRoles(userRoles);
      setUserName(userData.userName);
      setEmail(userData.email);
      setShowModal(true);
    } catch (err) {
      showToast('Failed to load user details.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Handle the creation of a new user
  const handleAddNewUser = () => {
    setIsNewUser(true);  // We're adding a new user
    setSelectedUser(null);
    setSelectedRoles([]);
    setUserName('');
    setEmail('');
    setInitialPassword('');  // Reset initial password field
    setNewPassword('');
    setShowPasswordField(false); // Hide password field for new users
    setShowModal(true);
  };

  // Handle saving changes when the form is submitted
  const handleSave = async () => {
    // Validate initial password for new users
    if (isNewUser && !validatePassword(initialPassword)) {
      setInitialPasswordError(true);
      return;
    }

    setInitialPasswordError(false);
    setLoading(true);
    try {
      if (isNewUser) {
        let response = await createUser(userName, email, initialPassword, selectedRoles);
        if (response && response.isSuccessful) {
          showToast("User Created", "success");
          setShowModal(false);
          // Refresh the users list
          const usersData = await getUsers();
          setUsers(usersData);
        } else {
          showToast("Failed to create user: " + (response?.message || "Unknown error"), "error");
        }
      }
      else {
        let response = await updateUser(userName, email, selectedRoles);
        if (response && response.isSuccessful) {
          showToast("User Updated", "success");
          setShowModal(false);
          // Refresh the users list
          const usersData = await getUsers();
          setUsers(usersData);
        } else {
          showToast("Failed to update user: " + (response?.message || "Unknown error"), "error");
        }
      }


    } catch (err) {
      if (isNewUser) {
        showToast('Failed to create user: ' + err.message, 'error');
      } else {
        showToast('Failed to update user: ' + err.message, 'error');
      }
    } finally {
      setLoading(false);
    }
  };


  //Handle password validation
  const validatePassword = (password) => {
    const minLength = password.length >= 6;
    const hasUpper = /[A-Z]/.test(password);
    const hasLower = /[a-z]/.test(password);
    const hasDigit = /[0-9]/.test(password);
    const hasNonAlpha = /[^A-Za-z0-9]/.test(password);

    return minLength && hasUpper && hasLower && hasDigit && hasNonAlpha;
  };

  const passwordIsValid = validatePassword(newPassword);
  const initialPasswordIsValid = validatePassword(initialPassword);

  // Handle resetting password
  const handleResetPassword = async () => {
    if (!validatePassword(newPassword)) {
      setPasswordError(true);
      return; // Prevent password reset
    }
    setPasswordError(false); // Clear any previous errors
    setLoading(true);
    try {
      // Stub: Reset password via the API
      await resetPassword(selectedUser.id, newPassword);
      setSuccess(true);
      setShowPasswordField(false);  // Hide password field after successful reset
      setNewPassword('');
    } catch (err) {
      showToast('Failed to reset password: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Handle modal close
  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedUser(null);
    setSelectedRoles([]);
    setShowPasswordField(false);  // Reset the password field visibility on close
    setNewPassword('');
    setUserName('');
    setEmail('');
    setInitialPassword('');  // Reset initial password field
  };

  // Stub API functions
  const getUsers = async () => {
    const adminClient = new AdminClient();
    return await adminClient.getUsers();
  };

  const getUserById = async (id) => {
    const adminClient = new AdminClient();
    return await adminClient.getUserById(id);
  };

  const getUserRolesByUserId = async (id) => {
    const adminClient = new AdminClient();
    return await adminClient.getUserRolesByUserId(id);
  };

  const updateUser = async (userName, email, roles) => {
    const adminClient = new AdminClient();
    return await adminClient.updateUser(selectedUser.id, { userName, email, roles });
  };

  const createUser = async (userName, email, password, roles) => {
    const adminClient = new AdminClient();
    return await adminClient.createUser({ userName, email, password, roles });
  };

  const getRoles = async () => {
    const adminClient = new AdminClient();
    return await adminClient.getRoles();
  };

  const resetPassword = async (userId, password) => {
    const adminClient = new AdminClient();
    return await adminClient.resetPassword(userId, password);  // Assume an API call here
  };

  const deleteUser = async (userId) => {
    const adminClient = new AdminClient();
    try {

      await adminClient.deleteUser(userId);
      showToast("User Deleted", "success");
      // Refresh the users list
      const usersData = await getUsers();
      setUsers(usersData);
    } catch (err) {
      setError('Failed to delete user.');
    } finally {
      // Always close modal and refresh, regardless of error
      setShowModal(false);
      const usersData = await getUsers();
      setUsers(usersData);
    }
  }

  if (!isDealerAdmin) {
    return (
      <div className="container mt-5">
        <BackButton />
        <h2>User Management</h2>
        <Alert variant="warning" className="mt-3">
          You don't have permission to view this page. Dealer admin access is required.
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mt-5">
      <BackButton />
      <h2>User Management</h2>
      {loading ? (
        <Loader />
      ) : (
        <>
          <Button variant="success" onClick={handleAddNewUser} className="mb-3">
            Add New User
          </Button>
          <table className="table table-hover align-middle">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Roles</th> {/* New Roles Column */}
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr
                  key={user.id}
                  onClick={() => handleEditUser(user.id)}
                  style={{ cursor: 'pointer' }}  // Indicate row is clickable
                  className="clickable-row"
                >
                  <td>{user.userName}</td>
                  <td>{user.email}</td>
                  <td>
                    {/* Render Roles as Badges */}
                    {user.roles && user.roles.map(role => (
                      <span key={role} className="badge bg-secondary me-1">{role}</span>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Edit / Add User Modal */}
          <Modal show={showModal} onHide={handleCloseModal} centered size="md" backdrop="static">
            <Modal.Header closeButton>
              <Modal.Title>{isNewUser ? 'Add New User' : 'Edit User'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form>
                All fields are required.
                {/* User Name Input */}
                <Form.Group controlId="userName" className="mb-3 mt-3">
                  <Form.Label>Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                    placeholder="Enter full user name"
                    className="w-100"
                    required
                  />
                </Form.Group>

                {/* Email Input */}
                <Form.Group controlId="email" className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter email"
                    className="w-100"
                    required
                  />
                </Form.Group>

                {/* Initial Password Input (only for new user) */}
                {isNewUser && (
                  <Form.Group controlId="initialPassword" className="mb-3">
                    <Form.Label>Initial Password</Form.Label>
                    <Form.Control
                      type="password"
                      value={initialPassword}
                      onChange={(e) => setInitialPassword(e.target.value)}
                      placeholder="Enter initial password"
                      className="w-100"
                      required
                      isInvalid={initialPassword.length > 0 && !initialPasswordIsValid}

                    />
                    <Form.Control.Feedback type="invalid">
                      Password must be at least 6 characters and include:
                      uppercase, lowercase, number, and special character.
                    </Form.Control.Feedback>
                  </Form.Group>
                )}

                {/* Roles Section with Toggle Switches */}
                <Form.Group controlId="roles" className="mb-3">
                  <Form.Label>Roles (Select at least one)</Form.Label>
                  <div className="d-flex flex-column">
                    {availableRoles.map((role) => (
                      //dont show the Administrator role
                      role === 'Administrator' ? null : (
                        <Form.Check
                          key={role}
                          type="switch"
                          id={`role-${role}`}
                          label={role}
                          checked={selectedRoles.includes(role)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedRoles([...selectedRoles, role]);
                            } else {
                              setSelectedRoles(selectedRoles.filter((r) => r !== role));
                            }
                          }}
                          className="mb-2"
                        />)
                    ))}
                  </div>
                </Form.Group>

                {/* Reset Password Link */}
                {!isNewUser && <Form.Group className="mb-3">
                  <Button
                    variant="link"
                    onClick={() => setShowPasswordField(!showPasswordField)}
                    className="text-decoration-none"
                  >
                    {showPasswordField ? 'Cancel' : 'Reset Password'}
                  </Button>
                </Form.Group>}

                {/* New Password Field */}
                {showPasswordField && (
                  <Form.Group controlId="newPassword" className="mb-3">
                    <Form.Label>New Password</Form.Label>
                    <Form.Control
                      type="password"
                      value={newPassword}
                      onChange={(e) => {
                        setNewPassword(e.target.value)
                        // Clear error when user starts typing
                        if (passwordError) {
                          setPasswordError(false);
                        }
                      }}
                      placeholder="Enter new password"
                      className="w-100"
                      isInvalid={!passwordIsValid}
                    />
                    <Form.Control.Feedback type="invalid">
                      Password must be at least 6 characters and include:
                      uppercase, lowercase, number, and special character.
                    </Form.Control.Feedback>
                    <Button
                      variant="warning"
                      onClick={handleResetPassword}
                      className="mt-2 w-100"
                    >
                      Reset Password
                    </Button>
                  </Form.Group>
                )}

                <Form.Group className="mb-3">
                  {!isNewUser &&
                    <Button
                      variant="button"
                      onClick={() => {
                        if (window.confirm(`Are you sure you want to delete ${selectedUser.userName}?`)) {
                          deleteUser(selectedUser.id);
                          setShowModal(false); // close modal after deletion
                        }
                      }}
                      className="text-danger"
                    >
                      Delete User
                    </Button>}

                </Form.Group>
              </Form>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="primary" onClick={handleSave} disabled={loading || !userName || !email || !selectedRoles.length || (isNewUser && !initialPasswordIsValid)} className="w-100">
                {loading ? 'Saving...' : isNewUser ? 'Create User' : 'Save Changes'}
              </Button>
            </Modal.Footer>
          </Modal>
        </>
      )}
    </div>
  );
};

export default UsersPage;
